import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import NoActiveProject from '../../components/NoActiveProject';
import { useToolAccess } from '../../tools/ToolAccessContext';
import { isToolEntitled, tools } from '../../tools/catalog';
import { getFamilyShareInfo } from './utils/projectShareUtils';

const shellStyle = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem 1.25rem',
    boxSizing: 'border-box',
    overflow: 'hidden',
    gap: '0.75rem'
};

function formatCount(loading, value) {
    return loading ? '—' : String(value);
}

function StatGrid({ stats }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem', flexShrink: 0 }}>
            {stats.map(stat => (
                <div key={stat.label} style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    padding: '1rem',
                    borderRadius: 'var(--radius)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    minWidth: 0
                }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                    <div style={{ fontSize: stat.compact ? '1.15rem' : '1.4rem', fontWeight: 700, marginTop: '0.25rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stat.value}</div>
                    {stat.change && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            <i className="fa-solid fa-circle"></i>
                            <span>{stat.change}</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function OverviewTable({ columns, rows, emptyMessage, loading }) {
    const columnCount = columns.headers.length;
    return (
        <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{columns.title}</h3>
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
                <table className="data-table">
                    <thead>
                        <tr>
                            {columns.headers.map(col => (
                                <th key={col}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td className="cell-empty" colSpan={columnCount}>Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td className="cell-empty" colSpan={columnCount}>{emptyMessage}</td></tr>
                        )}
                        {rows.map((row) => (
                            <tr
                                key={row.id}
                                className="table-row-hover clickable-row"
                                onClick={row.onOpen}
                                onKeyDown={(event) => {
                                    if (event.target !== event.currentTarget) return;
                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                    event.preventDefault();
                                    row.onOpen();
                                }}
                                tabIndex={0}
                            >
                                {row.cells.map((cell, cellIndex) => (
                                    <td key={cellIndex} className={cellIndex === 0 ? 'cell-strong' : undefined}>
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatusBadge({ active, activeLabel, inactiveLabel }) {
    return (
        <span className={`status-chip${active ? ' is-positive' : ''}`}>{active ? activeLabel : inactiveLabel}</span>
    );
}

export default function ProjectToolPage({ toolKey }) {
    const navigate = useNavigate();
    const { accessMap, isSuperAdmin, accessReady } = useToolAccess();
    const tool = tools[toolKey];
    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem('activeProjectId') || '');
    const [activeProjectName, setActiveProjectName] = useState(localStorage.getItem('activeProjectName') || '');
    const [loading, setLoading] = useState(true);
    const [families, setFamilies] = useState([]);
    const [projects, setProjects] = useState([]);
    const [deployments, setDeployments] = useState([]);

    const load = useCallback(async () => {
        const projectId = localStorage.getItem('activeProjectId') || '';
        setActiveProjectId(projectId);
        setActiveProjectName(localStorage.getItem('activeProjectName') || '');
        if (!projectId || toolKey === 'Variant' || toolKey === 'Pricing') {
            setFamilies([]);
            setProjects([]);
            setDeployments([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            if (toolKey === 'Logic') {
                const [projectRes, familyRes] = await Promise.all([
                    apiFetch('/api/projects'),
                    apiFetch(`/api/products?projectId=${projectId}`)
                ]);
                setProjects(projectRes.ok ? await projectRes.json() : []);
                setFamilies(familyRes.ok ? await familyRes.json() : []);
            } else if (toolKey === 'Deployment') {
                const res = await apiFetch(`/api/deployments?projectId=${projectId}`);
                setDeployments(res.ok ? await res.json() : []);
            }
        } catch (error) {
            console.error(`Failed to load ${toolKey} overview`, error);
            setFamilies([]);
            setProjects([]);
            setDeployments([]);
        } finally {
            setLoading(false);
        }
    }, [toolKey]);

    useEffect(() => {
        load();
        window.addEventListener('activeProjectChanged', load);
        return () => window.removeEventListener('activeProjectChanged', load);
    }, [load]);

    const logicRows = useMemo(() => families.map(family => {
        const shareInfo = getFamilyShareInfo(family.id, projects);
        const ruleCount = family.ruleSets?.length || family.ruleSetCount || 0;
        return {
            id: family.id,
            shareInfo,
            ruleCount,
            cells: [
                family.name,
                String(ruleCount),
                <StatusBadge key="share" active={shareInfo.isShared} activeLabel="Shared" inactiveLabel="Exclusive" />
            ],
            onOpen: () => navigate(`/products?familyId=${family.id}`)
        };
    }), [families, projects, navigate]);

    const deploymentRows = useMemo(() => deployments.map(deployment => ({
        id: deployment.id,
        cells: [
            deployment.name,
            deployment.productFamilyName || '—',
            <StatusBadge key="status" active={deployment.isActive} activeLabel="Active" inactiveLabel="Inactive" />
        ],
        onOpen: () => navigate('/deploy')
    })), [deployments, navigate]);

    if (!accessReady) {
        return <div style={shellStyle}><p style={{ color: 'var(--text-secondary)' }}>Loading…</p></div>;
    }

    if (!isToolEntitled(accessMap, toolKey, isSuperAdmin)) {
        return (
            <div style={shellStyle}>
                <h1 style={{ fontSize: '1.35rem', margin: 0 }}>{tool?.label || toolKey}</h1>
                <p style={{ color: 'var(--text-secondary)' }}>You do not have access to this tool.</p>
            </div>
        );
    }

    if (!activeProjectId) {
        return (
            <div style={{ position: 'absolute', inset: 0 }}>
                <NoActiveProject description={`Select a project in the header to see its ${tool.label} overview.`} />
            </div>
        );
    }

    const sharedCount = logicRows.filter(row => row.shareInfo.isShared).length;
    const ruleTableCount = logicRows.reduce((total, row) => total + row.ruleCount, 0);
    const activeDeployments = deployments.filter(deployment => deployment.isActive).length;
    const projectLabel = activeProjectName || 'the selected project';

    const content = {
        Logic: {
            description: `Families and rule tables linked to ${projectLabel}.`,
            stats: [
                { label: 'Linked Families', value: formatCount(loading, logicRows.length), change: 'In this project' },
                { label: 'Rule Tables', value: formatCount(loading, ruleTableCount), change: 'Across linked families' },
                { label: 'Shared Families', value: formatCount(loading, sharedCount), change: 'Also used by other projects' }
            ],
            columns: { title: 'Families', headers: ['Name', 'Rule tables', 'Sharing'] },
            rows: logicRows,
            emptyMessage: 'No families linked to this project.'
        },
        Deployment: {
            description: `Deployments published for ${projectLabel}.`,
            stats: [
                { label: 'Deployments', value: formatCount(loading, deployments.length), change: 'In this project' },
                { label: 'Active', value: formatCount(loading, activeDeployments), change: 'Tokens currently active' },
                { label: 'Average Traffic', value: 'Not available yet', change: 'Usage analytics are not collected yet', compact: true }
            ],
            columns: { title: 'Deployments', headers: ['Name', 'Family', 'Status'] },
            rows: deploymentRows,
            emptyMessage: 'No deployments for this project.'
        },
        Variant: {
            description: `Variant data for ${projectLabel} is not available yet.`,
            stats: [
                { label: 'Variations', value: 'Not available yet', compact: true },
                { label: 'Configurations', value: 'Not available yet', compact: true },
                { label: 'Bulk Jobs', value: 'Not available yet', compact: true }
            ],
            columns: { title: 'Variants', headers: ['Name', 'Status', 'Updated'] },
            rows: [],
            emptyMessage: 'Variant has no project data yet.'
        },
        Pricing: {
            description: `Price data for ${projectLabel} is not available yet.`,
            stats: [
                { label: 'Price Books', value: 'Not available yet', compact: true },
                { label: 'Rate Cards', value: 'Not available yet', compact: true },
                { label: 'Discounts', value: 'Not available yet', compact: true }
            ],
            columns: { title: 'Prices', headers: ['Name', 'Status', 'Updated'] },
            rows: [],
            emptyMessage: 'Price has no project data yet.'
        }
    }[toolKey];

    return (
        <div style={shellStyle} className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: 'var(--radius)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <i className={tool.icon} style={{ fontSize: '1.1rem', color: 'var(--accent)' }}></i>
                </div>
                <div>
                    <h1 style={{ fontSize: '1.35rem', margin: 0, fontWeight: 700 }}>{tool.label}</h1>
                    <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0 0', fontSize: '0.95rem' }}>{content.description}</p>
                </div>
            </div>
            <StatGrid stats={content.stats} />
            <OverviewTable
                columns={content.columns}
                rows={content.rows}
                emptyMessage={content.emptyMessage}
                loading={loading && toolKey !== 'Variant' && toolKey !== 'Pricing'}
            />
        </div>
    );
}
